import { createCloudRunAxios } from './axios';

const api = createCloudRunAxios('remi-api');

export type RemiInstanceStatus = 'active' | 'paused' | 'disabled';

export interface RemiInstanceRecord {
  organization_id: string;
  instance_id: string;
  instance_type: 'personal' | 'contact_concierge';
  subject_user_id?: string | null;
  name: string;
  status: RemiInstanceStatus;
  runtime_mode: 'agentic' | 'shadow' | 'legacy';
  configuration_version: number;
  wake_enabled: boolean;
  learning_enabled: boolean;
  automatic_promotion_enabled: boolean;
  updated_at?: string;
}

export interface RemiRunRecord {
  run_id: string;
  request_id?: string | null;
  session_id?: string | null;
  task_id?: string | null;
  trigger_type: 'message' | 'wake' | 'replay';
  execution_mode: string;
  actor_type: string;
  actor_id: string;
  relationship_lane: string;
  role_name: string;
  role_tier: number;
  status: string;
  current_phase: string;
  failure_class?: string | null;
  failure_detail?: string | null;
  started_at?: string;
  completed_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface RemiActivityRecord {
  activity_id: string;
  sequence_number: number;
  activity_type: string;
  activity_name: string;
  status: string;
  evidence_reference?: string | null;
  external_effect: boolean;
  retry_safe: boolean;
  attempt_count: number;
  payload?: Record<string, unknown>;
  failure_detail?: string | null;
  started_at?: string;
  completed_at?: string | null;
}

export interface RemiConfirmationRecord {
  confirmation_id: string;
  tool_name: string;
  action_name: string;
  action_digest: string;
  status: string;
  response_text?: string | null;
  requested_at: string;
  responded_at?: string | null;
  expires_at: string;
  consumed_by_run_id?: string | null;
}

export interface RemiOutboxRecord {
  outbox_id: string;
  activity_id: string;
  destination: string;
  operation: string;
  status: string;
  attempt_count: number;
  next_attempt_at: string;
  provider_receipt?: Record<string, unknown> | null;
  failure_detail?: string | null;
  delivered_at?: string | null;
}

export interface RemiRunDetail extends RemiRunRecord {
  idempotency_key: string;
  pinned_bundle: Record<string, unknown>;
  authorization_envelope: Record<string, unknown>;
  activities: RemiActivityRecord[];
  confirmations: RemiConfirmationRecord[];
  outbox: RemiOutboxRecord[];
  commit?: Record<string, unknown> | null;
}

export interface RemiWakeState {
  generation: number;
  status: string;
  next_wakeup_at?: string | null;
  wake_reason?: string | null;
  lease_expires_at?: string | null;
  last_completion_status?: string | null;
  updated_at?: string;
}

const orgParams = (organizationId: string) => ({ organization_id: organizationId });

export async function listRemiInstances(organizationId: string): Promise<RemiInstanceRecord[]> {
  const { data } = await api.get('/api/v1/remi/instances', {
    params: orgParams(organizationId),
  });
  return data.instances ?? [];
}

export async function listRemiRuns(
  organizationId: string,
  instanceId: string
): Promise<RemiRunRecord[]> {
  const { data } = await api.get(`/api/v1/remi/instances/${instanceId}/runs`, {
    params: { ...orgParams(organizationId), limit: 200 },
  });
  return data.runs ?? [];
}

export async function getRemiRun(
  organizationId: string,
  instanceId: string,
  runId: string
): Promise<RemiRunDetail> {
  const { data } = await api.get(`/api/v1/remi/instances/${instanceId}/runs/${runId}`, {
    params: orgParams(organizationId),
  });
  return data;
}

export async function getRemiWakeState(
  organizationId: string,
  instanceId: string
): Promise<RemiWakeState | null> {
  const { data } = await api.get(`/api/v1/remi/instances/${instanceId}/wake-state`, {
    params: orgParams(organizationId),
  });
  return data.wake_state ?? null;
}

export async function setRemiInstanceStatus(
  organizationId: string,
  instanceId: string,
  action: 'pause' | 'resume',
  reason: string
): Promise<RemiInstanceRecord> {
  const { data } = await api.post(
    `/api/v1/remi/instances/${instanceId}:${action}`,
    { reason },
    { params: orgParams(organizationId) }
  );
  return data;
}
